import { Component, Inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormField, MatLabel } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatRadioButton, MatRadioGroup } from '@angular/material/radio';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CronEditorModule, CronOptions } from '@winarg/ngx-cron-editor';
import dayjs from 'dayjs';
import { LocaleService } from 'ngx-daterangepicker-material';
import { startWith } from 'rxjs';
import { DateRequiredValidator } from '../../../../core/validators/date-required.validator';
import { CustomMatErrorComponent } from '../../../../shared/components/custom-mat-error/custom-mat-error.component';
import { DateRangePickerComponent } from '../../../../shared/components/date-range-picker/date-range-picker.component';
import {
  IDateRangePickerConfiguration,
  IDateRangePickerDate,
} from '../../../../shared/components/date-range-picker/date-range-picker.interface';
import {
  ERecursionLimitType,
  IRecurrenceConfigurationInjectData,
  IRecurringItemFormGroup,
} from './recurring-item-configuration.interface';

@Component({
  imports: [
    ReactiveFormsModule,
    CronEditorModule,
    TranslatePipe,
    MatRadioGroup,
    MatLabel,
    MatRadioButton,
    DateRangePickerComponent,
    MatFormField,
    MatInput,
    MatDialogTitle,
    MatDialogContent,
    MatButton,
    MatDialogActions,
    MatDialogClose,
    CustomMatErrorComponent,
  ],
  providers: [LocaleService],
  selector: 'app-recurring-item-configuration',
  styleUrl: './recurring-item-configuration.component.scss',
  templateUrl: './recurring-item-configuration.component.html',
})
export class RecurringItemConfigurationComponent implements OnInit {
  public readonly cronOptions: CronOptions = {
    cronFlavor: 'standard',
    defaultTime: '00:00:00',
    formCheckboxClass: 'disabled',
    formInputClass: 'disabled',
    formRadioClass: 'disabled',
    formSelectClass: 'disabled',
    hideAdvancedTab: true,
    hideDailyTab: false,
    hideHourlyTab: true,
    hideMinutesTab: true,
    hideMonthlyTab: false,
    hideSeconds: true,
    hideSpecificMonthWeekTab: false,
    hideSpecificWeekDayTab: false,
    hideWeeklyTab: false,
    hideYearlyTab: false,
    use24HourTime: true,
  };

  public readonly datePickerConfiguration: IDateRangePickerConfiguration = {
    drops: 'up',
    isTimePickerShown: false,
    label: this.translate.instant('field.endDate'),
    minDate: dayjs.tz(),
    opens: 'center',
    showClearButton: false,
    singleDatePicker: true,
  };

  public readonly formGroup: FormGroup<IRecurringItemFormGroup> = new FormGroup({
    cron: new FormControl<string | null>(null, []),
    endDateLimit: new FormControl<IDateRangePickerDate | null | undefined>(undefined, [
      Validators.required,
      DateRequiredValidator(),
    ]),
    numberLimit: new FormControl<number | null>(null, [Validators.required, Validators.min(2), Validators.max(730)]),
    selectedLimitType: new FormControl<ERecursionLimitType>(ERecursionLimitType.never, [Validators.required]),
  });

  public readonly ERecursionLimitType = ERecursionLimitType;

  constructor(
    @Inject(MAT_DIALOG_DATA) public readonly injectData: IRecurrenceConfigurationInjectData,
    private readonly translate: TranslateService,
    public readonly dialogRef: MatDialogRef<RecurringItemConfigurationComponent>,
  ) {}

  public ngOnInit(): void {
    this.formGroup.controls.selectedLimitType.valueChanges
      .pipe(startWith(this.formGroup.controls.selectedLimitType.value))
      .subscribe((selectedLimitType) => {
        this.formGroup.controls.endDateLimit.disable();
        this.formGroup.controls.numberLimit.disable();

        if (selectedLimitType === ERecursionLimitType.endDateLimit) {
          this.formGroup.controls.endDateLimit.enable();
        } else if (selectedLimitType === ERecursionLimitType.numberLimit) {
          this.formGroup.controls.numberLimit.enable();
        }
      });

    this.formGroup.reset({
      ...this.injectData.formGroup.value,
      selectedLimitType: this.injectData.formGroup.value.selectedLimitType ?? ERecursionLimitType.never,
    });
  }

  public submitCron(): void {
    if (!this.formGroup.valid) {
      return;
    }

    this.injectData.formGroup.reset({
      ...this.formGroup.value,
      cron: (this.formGroup.controls.cron.value ?? '0 1 * * *')
        .replaceAll('\n', '')
        .split(' ')
        .filter(Boolean)
        .join(' '),
    });
    this.dialogRef.close();
  }

  public clearCron(): void {
    this.injectData.formGroup.reset({
      cron: null,
      endDateLimit: null,
      numberLimit: null,
      selectedLimitType: null,
    });
    this.dialogRef.close();
  }
}
